<script lang="ts" setup>
import {computed, defineAsyncComponent, onMounted, ref} from 'vue'
import {useKanbanStore} from '@src/stores/kanban'
import {useChatSessions} from '@src/composables/useChatSessions'
import useStore from '@src/stores/store'
import SessionList from '@src/components/shared/session/SessionlistKanban.vue'
const KanbanSendMessageModal = defineAsyncComponent(() => import("@src/components/shared/modals/KanbanSendMessageModal/KanbanSendMessageModal.vue"))
import OptionScheduleMessage from "@src/components/views/HomeView/MainArea/FooterArea/OptionsSelector/OptionScheduleMessage.vue"
const WaapiCampaign = defineAsyncComponent(() => import("@src/components/shared/modals/WaapiCampaignModal/WaapiCampaign.vue"))
import {useWAAPIStore} from "@src/stores/waapi";
import InformationIcon from "@src/components/ui/utils/InformationIcon.vue";

const store = useStore()
const kanbanStore = useKanbanStore()
const waapiStore = useWAAPIStore()

const { getSessionsByStatus, moveSessionToKanban } = useChatSessions()
const activeSessions = getSessionsByStatus('active')

const search = ref<string>('')

const filteredSessions = computed(() => {
    if (!search.value) {
        return activeSessions.value
    }

    return activeSessions.value?.filter(session =>
        session.costumer.name
            .toLowerCase()
            .includes(search.value.toLowerCase())
    )
})

const sessionsByKanban = computed(() => {
    const grouped: Record<string | number, any[]> = {}

    for (const session of filteredSessions.value ?? []) {
        const kanbanId = session.kanban_card ?? '0'

        if (!grouped[kanbanId]) {
            grouped[kanbanId] = []
        }

        grouped[kanbanId].push(session)
    }

    return grouped
})

const draggedSession = ref<any | null>(null)

function onSessionDragStart(session: any) {
    draggedSession.value = session
}

async function onSessionDrop(targetKanbanId: number) {
    if (!draggedSession.value) return

    if (draggedSession.value.kanban_card === targetKanbanId) {
        draggedSession.value = null
        return
    }

    await moveSessionToKanban(
        draggedSession.value.id,
        targetKanbanId
    )

    draggedSession.value = null
}

function allowDrop(event: DragEvent) {
    event.preventDefault()
}

const kanbanContainerRef = ref<HTMLElement | null>(null)

function onKanbanDragOver(event: DragEvent) {
    event.preventDefault()

    const container = kanbanContainerRef.value
    if (!container) return

    const rect = container.getBoundingClientRect()
    const scrollSpeed = 12
    const threshold = 80

    if (event.clientX > rect.right - threshold) {
        container.scrollLeft += scrollSpeed
    }

    if (event.clientX < rect.left + threshold) {
        container.scrollLeft -= scrollSpeed
    }
}

const showSendMessageModal = ref(false)
const selectedKanbanSession = ref<any | null>(null)
const showScheduleMessageModal = ref(false)
const selectedScheduleSession = ref<any | null>(null)
const selectedCampaignSession = ref<any | null>(null)

function openCampaignModal(session: any) {
    selectedCampaignSession.value = session

    waapiStore.cleanCampaign()
    waapiStore.selectedChannel = session.channel
    waapiStore.campaignForm.channel_id = session.channel?.id ?? 0
    waapiStore.costumerToken = session.costumer?.token ?? null
    waapiStore.showCampaignModal = true
}

function openSendMessageModal(session: any) {
    selectedKanbanSession.value = session
    showSendMessageModal.value = true
}

function closeSendMessageModal() {
    showSendMessageModal.value = false
    selectedKanbanSession.value = null
}

function openScheduleMessageModal(session: any) {
    selectedScheduleSession.value = session
    showScheduleMessageModal.value = true
}

function closeScheduleMessageModal() {
    showScheduleMessageModal.value = false
    selectedScheduleSession.value = null
}

onMounted(() => {
    kanbanStore.loadKanbans()
})

</script>

<template>
    <div class="p-2 w-full md:p-6 flex flex-col min-h-0 h-full">
        <div class="flex flex-shrink-0">
            <h1 class="heading-1 text-black/70 dark:text-white/70 outline-none mt-3 mb-7 ">Visualização Kanban</h1>
          <svg class="mt-2 ml-5" fill="#8e8d8d" height="24px" viewBox="0 -960 960 960" width="24px" xmlns="http://www.w3.org/2000/svg"><path d="M280-280h80v-400h-80v400Zm320-80h80v-320h-80v320ZM440-480h80v-200h-80v200ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z"/></svg>
          <InformationIcon title="Visualização Kanban" text="Organize e acompanhe os atendimentos por etapas."></InformationIcon>
        </div>

        <div
            ref="kanbanContainerRef"
            class="flex overflow-auto kanban-scroll min-h-0 flex-1 py-4"
            @dragover="onKanbanDragOver"
        >
            <div
                v-for="(list, index) in kanbanStore.kanbans"
                :key="list.id"
                class="flex items-start"
            >
                <!-- Coluna -->
                <div
                    :style="{ backgroundColor: list.color }"
                    class="rounded-sm px-4 py-4 w-[280px] flex flex-col max-h-[100%] min-h-[140px] card-scroll"
                    @drop="onSessionDrop(list.id)"
                    @dragover.prevent
                >
                    <!-- Título -->
                    <p
                        :class="[
                'text-sm font-semibold mb-3 select-none break-words line-clamp-2',
                ['#000000', '#3b3b3b', '#206806','#0a3cba','#6b1fad', '#ff3a3a','#ff54bc','#ff751f'].includes(list.color)
                    ? 'text-gray-100'
                    : 'text-gray-600'
            ]"
                    >
                        {{ list.name }}
                    </p>

                    <!-- Sessions -->
                    <div class="flex flex-col gap-2 overflow-y-auto">
                        <SessionList
                            v-if="sessionsByKanban[list.id]?.length"
                            :sessions="sessionsByKanban[list.id]"
                            @drag-start="onSessionDragStart"
                            @send-message="openSendMessageModal"
                            @send-campaign="openCampaignModal"
                            @schedule-message="openScheduleMessageModal"
                        />

                        <div v-else class="text-xs text-gray-400 italic">
                            Nenhuma sessão
                        </div>
                    </div>
                </div>

                <!-- Seta -->
                <svg
                    v-if="index < kanbanStore.kanbans.length - 1"
                    class="flex-shrink-0 mx-2 mt-5"
                    fill="#8e8d8d"
                    height="24"
                    viewBox="0 -960 960 960"
                    width="24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M504-480 320-664l56-56 240 240-240 240-56-56 184-184Z"
                    />
                </svg>
            </div>
        </div>

        <Teleport v-if="showSendMessageModal" to="#modal-root">
            <KanbanSendMessageModal
                :open="showSendMessageModal"
                :session="selectedKanbanSession"
                :close-modal="closeSendMessageModal"
            />
        </Teleport>

        <Teleport v-if="showScheduleMessageModal" to="#modal-root">
            <Transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 translate-y-2"
            >
                <OptionScheduleMessage
                    :session="selectedScheduleSession"
                    @closeModal="closeScheduleMessageModal"
                />
            </Transition>
        </Teleport>

        <Teleport v-if="waapiStore.showCampaignModal && selectedCampaignSession" to="#modal-root">
            <WaapiCampaign
                :costumer-token="selectedCampaignSession?.costumer?.token"
                :session-id="selectedCampaignSession?.id"
            />
        </Teleport>
    </div>
</template>

<style scoped>
/* Chrome, Edge, Safari */
.kanban-scroll::-webkit-scrollbar {
    height: 20px;
}

.kanban-scroll::-webkit-scrollbar-track {
    background: rgba(86, 86, 86, 0.15);
    border-radius: 8px;
}

.kanban-scroll::-webkit-scrollbar-thumb {
    background: rgba(86, 86, 86, 0.45);
    border-radius: 8px;
}

/* hover = mesma cor do original */
.kanban-scroll::-webkit-scrollbar-thumb:hover,
.kanban-scroll::-webkit-scrollbar-thumb:active {
    background: rgba(86, 86, 86, 0.45);
}


/* Firefox */
.kanban-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(124, 140, 245, 0.90) rgba(86, 86, 86, 0.15);
}





.card-scroll:hover {
    scrollbar-width: thin;
    scrollbar-color: rgba(216, 216, 216, 0.9) rgba(86, 86, 86, 0.15);
}

.card-scroll::-webkit-scrollbar {
    height: 20px;
}

.card-scroll::-webkit-scrollbar-track {
    background: rgba(250, 250, 250, 0.15);
    border-radius: 8px;
}

.card-scroll::-webkit-scrollbar-thumb {
    background: rgba(250, 250, 250, 0.45);
    border-radius: 8px;
}

/* hover = mesma cor do original */
.card-scroll::-webkit-scrollbar-thumb:hover,
.card-scroll::-webkit-scrollbar-thumb:active {
    background: rgba(250, 250, 250, 0.45);
}


/* Firefox */
.card-scroll {
    scrollbar-width: thin;
    scrollbar-color: rgba(250, 250, 250, 0.90) rgba(250, 250, 250, 0.15);
}

.card-scroll:hover {
    scrollbar-width: thin;
    scrollbar-color: rgba(250, 250, 250, 0.90) rgba(86, 86, 86, 0.15);
}

</style>